import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ImportPreview, ImportResult, ImportSourceId, ImportStep } from '../../../models/test-case-import.model';
import { TestCaseImportService } from '../../../services/test-case-import.service';
import { FileDropzoneComponent } from '../../../components/file-dropzone/file-dropzone.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { saveBlob } from '../../../shared/file-download';

interface ImportSourceOption {
  id: ImportSourceId;
  available: boolean;
  icon: string;
}

const SOURCES: readonly ImportSourceOption[] = [
  { id: 'excel', available: true, icon: 'grid_on' },
  { id: 'xray', available: false, icon: 'shield' },
  { id: 'testrail', available: false, icon: 'bar_chart' },
];

const STEPS: readonly ImportStep[] = [1, 2, 3];

const ARROW_KEYS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];

const ACCEPTED_FILE_TYPES = '.csv,.xlsx,.xls';
const ACCEPTED_EXTENSIONS = ['csv', 'xlsx', 'xls'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

@Component({
  selector: 'app-test-case-import-modal',
  imports: [A11yModule, TranslatePipe, MatIcon, MatProgressSpinner, FileDropzoneComponent, SectionTitleComponent],
  templateUrl: './test-case-import-modal.component.html',
  styleUrl: './test-case-import-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseImportModalComponent {
  private readonly importService = inject(TestCaseImportService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly visible = model(false);
  readonly imported = output<ImportResult>();

  readonly steps = STEPS;
  readonly sources = SOURCES;
  readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;

  readonly step = signal<ImportStep>(1);
  readonly source = signal<ImportSourceId>('excel');
  readonly file = signal<File | null>(null);

  readonly busy = signal(false);
  readonly errorKey = signal<string | null>(null);

  readonly preview = signal<ImportPreview | null>(null);
  readonly result = signal<ImportResult | null>(null);

  readonly canProceed = computed(() => {
    if (this.busy()) return false;
    if (this.step() === 2) return this.file() !== null;
    return true;
  });

  private triggerElement: HTMLElement | null = null;

  constructor() {
    let wasVisible = false;
    effect(() => {
      const open = this.visible();
      if (open && !wasVisible) {
        this.triggerElement = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
      } else if (!open && wasVisible) {
        document.body.style.overflow = '';
        this.triggerElement?.focus();
        this.triggerElement = null;
      }
      wasVisible = open;
    });
    this.destroyRef.onDestroy(() => (document.body.style.overflow = ''));
  }

  rovingTabindex(option: ImportSourceOption): number {
    return option.available && this.source() === option.id ? 0 : -1;
  }

  countKey(base: string, count: number): string {
    return `testCases.import.preview.${base}${count === 1 ? 'One' : 'Other'}`;
  }

  selectSource(option: ImportSourceOption): void {
    if (!option.available) return;
    this.source.set(option.id);
  }

  onSourceKeydown(event: KeyboardEvent): void {
    if (!ARROW_KEYS.includes(event.key)) return;
    event.preventDefault();
    const available = this.sources.filter((s) => s.available);
    if (available.length === 0) return;
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const current = available.findIndex((s) => s.id === this.source());
    const next = available[(current + (forward ? 1 : -1) + available.length) % available.length];
    this.source.set(next.id);
    document.getElementById(`tci-source-${next.id}`)?.focus();
  }

  onFileSelected(file: File): void {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      this.file.set(null);
      this.errorKey.set('testCases.import.errors.fileType');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.file.set(null);
      this.errorKey.set('testCases.import.errors.fileTooLarge');
      return;
    }
    this.errorKey.set(null);
    this.file.set(file);
  }

  onFileCleared(): void {
    this.file.set(null);
  }

  primaryAction(): void {
    if (this.busy()) return;
    switch (this.step()) {
      case 1:
        this.step.set(2);
        return;
      case 2:
        this.runPreview();
        return;
      case 3:
        this.runCommit();
        return;
    }
  }

  back(): void {
    if (this.busy() || this.step() === 1) return;
    this.errorKey.set(null);
    this.setStep(this.step() - 1);
  }

  private setStep(value: number): void {
    const clamped = Math.min(Math.max(value, 1), 3);
    this.step.set(clamped as ImportStep);
  }

  downloadTemplate(): void {
    this.importService
      .downloadTemplate(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (download) => saveBlob(download.blob, download.filename),
        error: () => this.errorKey.set('testCases.import.errors.template'),
      });
  }

  close(): void {
    this.visible.set(false);
    this.reset();
  }

  private runPreview(): void {
    const file = this.file();
    if (!file) return;
    this.busy.set(true);
    this.errorKey.set(null);
    this.importService
      .preview(this.projectId(), file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preview) => {
          this.preview.set(preview);
          this.step.set(3);
          this.busy.set(false);
        },
        error: () => {
          this.errorKey.set('testCases.import.errors.preview');
          this.busy.set(false);
        },
      });
  }

  private runCommit(): void {
    const file = this.file();
    if (!file) return;
    this.busy.set(true);
    this.errorKey.set(null);
    this.importService
      .commit(this.projectId(), file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.result.set(result);
          this.imported.emit(result);
          this.busy.set(false);
        },
        error: () => {
          this.errorKey.set('testCases.import.errors.commit');
          this.busy.set(false);
        },
      });
  }

  private reset(): void {
    this.step.set(1);
    this.source.set('excel');
    this.file.set(null);
    this.busy.set(false);
    this.errorKey.set(null);
    this.preview.set(null);
    this.result.set(null);
  }
}
